import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TextButton as Button } from '@oneaudi/unified-web-components';
import {
  selectDealersList,
  clearInfoDetail,
  setConfirmedFilters,
  clearTempFilters,
  setToggleEdit,
} from '../../reducers/dealersListReducer';
import MapLayout from '../MapLayout';
import DealersList from '../DealersSelector/DealersList';
// import { useMediaQuery } from 'react-responsive';
import Modal from '../Modal';
import MapRadiusSlider from '../MapRadiusSlider';
import DealersSelector from '../DealersSelector';
import DealerInfo from '../DealerInfo';
import InventoryError from '../InventoryError';
import CloseModalIcon from '../../assets/icons/CloseModalIcon';
import { LsInterface } from '../../hooks/useLocationLocalStorage';
import { Coordinates, DealershipList, SelectedUserLocation } from '../../types/locations';
import { GeoJSONProperties } from '../../hooks/useDealershipList';
import { dealerSaveAnalyticsEvent } from '../../utils/analyticsEventListeners';
import {
  RadiusSliderWrapper,
  InfoContainer,
  DeaslersControllerWrapper,
  DealerInfoWrapper,
  CancelButtonWrapper,
} from './DealersModal.styles';
import { PlaceResultType } from '../../types/placeResultLocation';
// import DealersModalDesktopView from './DealersModalDesktopView';
// import DealersModalMobileView from './DealersModalMobileView';

type DealersModalProps = {
  onCloseModal: () => void;
  saveDealersToLs: (toSave?: LsInterface) => void;
};

const DealersModal: React.FC<DealersModalProps> = ({ onCloseModal, saveDealersToLs }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    infoDealer,
    centerLocation,
    dealersWithinRadius,
    isEdit,
    selectedDealersIds,
    radius,
    allDealersList,
  } = useSelector(selectDealersList);
  const [isDealerlistOpen, setIsDealerListOpen] = useState<boolean>(false);

  const handleSaveSelection = useCallback(() => {
    if (!centerLocation) return;
    let selectedDealers: DealershipList;
    if (!selectedDealersIds.length) {
      selectedDealers = dealersWithinRadius.map((dealer) => dealer.properties);
    } else {
      selectedDealers = (
        allDealersList as GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties>
      ).features
        .map((dealer) => dealer.properties)
        .filter((dealer) => selectedDealersIds.includes(dealer.id));
    }

    const userCoordinates: Coordinates = {
      latitude: (centerLocation as PlaceResultType).geometry.location.lat as number,
      longitude: (centerLocation as PlaceResultType).geometry.location.lng as number,
    };

    const userLocation: SelectedUserLocation = {
      feature: centerLocation as PlaceResultType,
      userCoordinates,
    };

    saveDealersToLs({
      selectedSearchMode: selectedDealersIds.length ? 'BY_DEALERS' : 'BY_RADIUS',
      selectedRadius: radius,
      selectedUserLocation: userLocation,
      isPreSetPartner: false,
      selectedDealershipList: selectedDealers,
      isPartnerHeader: false,
    });

    dispatch(setConfirmedFilters());

    // Analytics DL event
    dealerSaveAnalyticsEvent(
      selectedDealers,
      radius.toString(),
      selectedDealersIds.length ? 'BY_DEALERS' : 'BY_RADIUS',
      dealersWithinRadius.length,
      userLocation || undefined,
    );
  }, [
    allDealersList,
    centerLocation,
    dealersWithinRadius,
    dispatch,
    radius,
    saveDealersToLs,
    selectedDealersIds,
  ]);

  const handleOpenDealersList = () => {
    // if edit address is open, close it
    if (!isEdit) {
      dispatch(setToggleEdit());
    }
    setIsDealerListOpen(true);
  };

  const handleCloseDealersList = () => {
    setIsDealerListOpen(false);
  };

  //   const isMobile = useMediaQuery({ maxWidth: 767 });

  const handleCloseModal = () => {
    dispatch(clearTempFilters());
    onCloseModal();
  };

  const handleViewInventory = () => {
    handleSaveSelection();
    onCloseModal();
  };

  const handleCloseDealerInfo = () => dispatch(clearInfoDetail());

  return (
    <Modal onClose={handleCloseModal} closableOnEscape={isEdit || !centerLocation}>
      <CancelButtonWrapper onClick={handleCloseModal} tabIndex={0}>
        <CloseModalIcon />
      </CancelButtonWrapper>
      <MapLayout />
      <InfoContainer isListOpened={isDealerlistOpen}>
        {Object.keys(infoDealer).length ? (
          <DealerInfoWrapper>
            <DealerInfo
              dealerInfo={infoDealer as GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties>}
              onClose={handleCloseDealerInfo}
            />
          </DealerInfoWrapper>
        ) : (
          <>
            {isDealerlistOpen ? (
              <>
                <div>
                  <DealersList
                    onClose={handleCloseDealersList}
                    isListOpened={isDealerlistOpen}
                    disabled={!centerLocation || !dealersWithinRadius.length}
                    handleViewInventory={handleViewInventory}
                  />
                </div>
              </>
            ) : (
              <>
                <RadiusSliderWrapper>
                  <MapRadiusSlider />
                  {!dealersWithinRadius?.length && centerLocation && <InventoryError />}
                </RadiusSliderWrapper>
                {!(!dealersWithinRadius?.length && centerLocation) && (
                  <DeaslersControllerWrapper>
                    <DealersSelector onOpen={handleOpenDealersList} />
                  </DeaslersControllerWrapper>
                )}

                {/* <DeaslersControllerWrapper>
                      <DealersSelector onOpen={handleOpenDealersList} />
                    </DeaslersControllerWrapper> */}
              </>
            )}
            <Button
              disabled={!centerLocation || !dealersWithinRadius.length}
              variant="primary"
              isFullWidth
              onClick={handleViewInventory}
              className="viewInventoryButton"
            >
              {t('dealerLocationSearch.dealersList.save')}
            </Button>
          </>
        )}
      </InfoContainer>
      {/* {!isMobile ? (
        <DealersModalDesktopView
          onClose={onClose}
          selectedDealerIds={selectedDealerIds}
          handleDealershipToggle={handleDealershipToggle}
          handleSaveSelection={handleSaveSelection}
        />
      ) : (
        <DealersModalMobileView
          onClose={onClose}
          selectedDealerIds={selectedDealerIds}
          handleDealershipToggle={handleDealershipToggle}
          handleSaveSelection={handleSaveSelection}
        />
      )} */}
    </Modal>
  );
};

export default DealersModal;
